import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import {
    ChartDataPoint,
    ChartdataSeries,
    ICardData,
    IPipelineApproachingLockExpiration,
    IPipelineLoanStatus,
    IPipelineParameters,
} from 'app/modules/pipeline/interfaces/pipeline-data';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import { PipelineLockStatusChartOptions } from 'app/modules/shared/classes/pipeline-lock-status-insights-chart-options';
import * as Highcharts from 'highcharts';
import { IChartTitleData } from 'app/modules/shared/interfaces/card-data';
import { Router } from '@angular/router';
import { PipelineService } from 'app/modules/pipeline/services/pipeline.service';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import { Observable } from 'rxjs';
import { ITableDataItems } from 'app/modules/shared/components/simple-table-card/simple-table-card.component';
import { SimpleTableCardService } from 'app/modules/my-limegear/components/standalone-components/services/simple-table-card.service';

@Injectable({
    providedIn: 'root',
})
export class PipelineLockStatusService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private componentName = 'pipeline';
    private _url = (this.serverUrl + '/api/v{version}/{componentName}')
        .replace('{version}', this.version)
        .replace('{componentName}', this.componentName);

    constructor(
        private _http: HttpClient,
        private _pipelineServices: PipelineService,
        private _reusableFunctions: ReusableFunctions,
        private _simpleDataTableService: SimpleTableCardService,
        private _userAccessService: UserAccessService,
        private _router: Router
    ) {}

    getPipelineLockStatusByMilestone(
        params: IPipelineParameters
    ): Observable<IPipelineLoanStatus[]> {
        return this._http.post<IPipelineLoanStatus[]>(
            this._url + '/GetPipelineLockStatusByMilestone',
            params,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    getPipelineLockStatusByLoanType(
        params: IPipelineParameters
    ): Observable<IPipelineLoanStatus[]> {
        return this._http.post<IPipelineLoanStatus[]>(
            this._url + '/GetPipelineLockStatusByLoanType',
            params,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    getPipelineLockExpirations(
        params: IPipelineParameters
    ): Observable<IPipelineApproachingLockExpiration[]> {
        return this._http.post<IPipelineApproachingLockExpiration[]>(
            this._url + '/GetPipelineLockExpirations',
            params,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    initializeCards(card: ICardData, action: NoDataActionType) {
        let url = card.drilldownUrl + card.urlSegment;
        card.hasAccess = this.checkAccess(url);
        card.navFromUrl = this._router.url.split('?')[0];
        let largeCardInteriorOptions = {
            action: action,
            componentTitle: card.title,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: true,
            accessToUrl: url,
            uniqueId: card.cardId,
            showCardControllers: true,
        };
        card.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    checkAccess(url: string) {
        return this._userAccessService.hasAccessToPage(url);
    }

    setAndGetParams(hFilterJSON: string, displayOptions: DisplayOptions[]): IPipelineParameters {
        if (displayOptions && displayOptions.length > 0) {
            try {
                let options: IPipelineParameters = {
                    channel: '',
                    loanPurpose: '',
                    loanType: '',
                    lockStatus: '',
                    hFilterJSON: '[]',
                    milestone: '',
                };

                options.hFilterJSON = hFilterJSON;
                options.channel =
                    displayOptions.find((item) => item.optionKey === 'channel')?.selectedOptions ??
                    'All';
                options.loanType =
                    displayOptions.find((item) => item.optionKey === 'loanCategory')
                        ?.selectedOptions ?? 'All';
                options.loanPurpose =
                    displayOptions.find((item) => item.optionKey === 'loanPurpose')
                        ?.selectedOptions ?? 'All';
                options.lockStatus =
                    displayOptions.find((item) => item.optionKey === 'loanLockStatus')
                        ?.selectedOptions ?? 'All';
                options.milestone =
                    displayOptions.find((x) => x.optionKey === 'activeMilestones')
                        ?.selectedOptions ?? 'All';
                return options;
            } catch (error) {
                return;
            }
        }
        return;
    }

    updateHeatmapData(
        data: IPipelineApproachingLockExpiration[],
        action: NoDataActionType,
        card: ICardData
    ) {
        let loanStatusChartOptions = new PipelineLockStatusChartOptions(
            this._router,
            this._reusableFunctions
        );

        let yProperty = 'loanUnits';
        let xProperty = 'date';
        let seriesDataProperty = 'clientMilestoneName';

        let seriesnames = this._reusableFunctions
            .getUniqueItemList(data, seriesDataProperty)
            .filter((item) => item !== null && item !== '');
        let xCategories = this._reusableFunctions
            .getUniqueItemList(data, xProperty)
            .filter((item) => item !== null && item !== '');
        let processedSeriesData = this.setHeatmapData(
            data,
            seriesnames,
            xCategories,
            xProperty,
            yProperty,
            seriesDataProperty
        );

        let chartOptions = loanStatusChartOptions.getHeatmapOptions(
            processedSeriesData,
            xCategories,
            seriesnames,
            card.title,
            card.hasAccess,
            card.drilldownUrl,
            card.urlSegment,
            card.navFromUrl
        );

        let largeCardInteriorOptions = card.largeCardInteriorOptions;
        largeCardInteriorOptions.chart = new Highcharts.Chart(
            largeCardInteriorOptions.uniqueId,
            chartOptions
        );
        largeCardInteriorOptions.hasData = data.length > 0;
        largeCardInteriorOptions.isDataReady = true;
        largeCardInteriorOptions.searchTerm = '';
        largeCardInteriorOptions.action = action;
        largeCardInteriorOptions.showCardControlArea = true;
        card.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    updateChartData(
        cardInfo: ICardData,
        data: ChartdataSeries[],
        hasData: boolean,
        isDataReady: boolean,
        searchTerm: string,
        action: NoDataActionType
    ) {
        let loanStatusChartOptions = new PipelineLockStatusChartOptions(
            this._router,
            this._reusableFunctions
        );

        let chartOptions = loanStatusChartOptions.getLockStatusByMilestoneOptions(
            null,
            '',
            'Volume ($)',
            data,
            cardInfo.drilldownUrl,
            cardInfo.urlSegment,
            true,
            cardInfo.hasAccess,
            true,
            cardInfo.navFromUrl
        );

        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        if (hasData) {
            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                chartOptions
            );
        }
        let queryParams = {
            activeMilestones: 'All',
            navFromUrl: cardInfo.navFromUrl,
        };

        let chartTitleData: IChartTitleData = {
            showChartCustomTitle: true,
            chartTile: cardInfo.title,
            chartMainValue: '',
            showMainValue: false,
            svgIconString: '',
        };
        largeCardInteriorOptions.hasData = hasData;
        largeCardInteriorOptions.isDataReady = isDataReady;
        largeCardInteriorOptions.searchTerm = searchTerm;
        largeCardInteriorOptions.action = action;
        largeCardInteriorOptions.accessToUrl =
            cardInfo.drilldownUrl + 'insights_pipelineByLoanType';
        largeCardInteriorOptions.chartTitleData = chartTitleData;
        largeCardInteriorOptions.queryParams = queryParams;
        largeCardInteriorOptions.showCardControlArea = true;
        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    setHeatmapData(
        dataSet: IPipelineApproachingLockExpiration[],
        seriesNames: string[],
        xCategories: string[],
        xProperty: string,
        yProperty: string,
        seriesDataProperty: string
    ) {
        let heatmapData: Highcharts.PointOptionsObject[] = [];
        seriesNames.forEach((seriesItem, seriesIdx) => {
            let seriesData = dataSet.filter((item) => item[seriesDataProperty] === seriesItem);
            if (seriesData) {
                xCategories.forEach((xItem, xIdx) => {
                    let value = seriesData.find((item) => item[xProperty] === xItem);
                    heatmapData.push({
                        x: xIdx,
                        y: seriesIdx,
                        value: value ? value[yProperty] : 0,
                        name: xItem,
                        custom: {
                            volume: value ? value['loanVolume'] : 0,
                            seriesName: seriesItem,
                            daysToExpiration: value.daysToExpiration,
                        },
                    });
                });
            }
        });
        return heatmapData;
    }

    getUniqueXValues(
        data: IPipelineLoanStatus[] | IPipelineApproachingLockExpiration[],
        xProperty: string
    ) {
        let uniqueXValueList: ISortingData[] = [];
        data.forEach((item) => {
            let hasValue = uniqueXValueList.findIndex((val) => val.xVal === item[xProperty]);
            if (hasValue === -1) {
                uniqueXValueList.push({
                    xVal: item[xProperty],
                    order: item.clientOrder,
                });
            }
        });

        return uniqueXValueList.sort((a, b) => a.order - b.order);
    }

    setStackChartData(
        data: IPipelineLoanStatus[],
        xProperty: string,
        seriesProperty: string,
        chartType: string
    ) {
        let seriesNames = this._reusableFunctions.getUniqueItemList(data, seriesProperty);
        let dataSet: IPipelineLoanStatus[] = data.map((obj) => ({ ...obj }));

        let lengendItemsIndex = {
            Lock: 0,
            Float: 1,
            CDW: 2,
            Expired: 3,
        };

        let chartItemsIndex = {
            Lock: 3,
            Float: 2,
            CDW: 1,
            Expired: 0,
        };

        let colorObject = {
            Lock: '#8F80DB',
            Float: '#4EB6B6',
            CDW: '#E6AC8F',
            Expired: '#E0000E',
        };

        let uniqueXList = this.getUniqueXValues(dataSet, xProperty);
        let stackColumnsData: ChartdataSeries[] = [];
        seriesNames.forEach((seriesName) => {
            if (chartType === 'bar' || chartType === 'column') {
                let classData = dataSet.filter((item) => item[seriesProperty] === seriesName);
                stackColumnsData.push({
                    name: seriesName,
                    data: this.setPointData(classData, xProperty, uniqueXList),
                    legendIndex: lengendItemsIndex[seriesName],
                    index: chartItemsIndex[seriesName],
                    color: colorObject[seriesName],
                    type: chartType,
                });
            }
        });
        return stackColumnsData;
    }

    setPointData(
        classData: IPipelineLoanStatus[],
        xProperty: string,
        uniqueXList: ISortingData[],
        yProperty: string = 'loanVolume'
    ) {
        let purposeItems: ChartDataPoint[] = [];

        uniqueXList.forEach((itm) => {
            let cData = classData.find((dat) => dat[xProperty] === itm.xVal);
            let pointItem: ChartDataPoint = {
                y: null,
                name: itm.xVal,
                properties: {
                    units: '',
                    loanType: 'All',
                    activeMilestones: itm.xVal,
                    lockStatus: 'All',
                    volume: '',
                },
            };

            if (cData) {
                pointItem.y = cData[yProperty];
                pointItem.properties.units = cData['loanUnits'] ?? '';
                pointItem.properties.loanType = xProperty === 'loanType' ? cData[xProperty] : 'All';
                pointItem.properties.activeMilestones =
                    xProperty === 'clientMilestoneName' ? cData[xProperty] : 'All';
                pointItem.properties.lockStatus = cData['lockStatus'] ?? '';
                pointItem.properties.volume = cData[yProperty] ?? '';
            }
            purposeItems.push(pointItem);
        });
        return purposeItems;
    }

    getPipelineLockStatusSimpleCardData(
        cardData: ICardData,
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        action: NoDataActionType,
        property: string
    ) {
        cardData.simpleDataTableData =
            this._simpleDataTableService.initializeSimpleDataCard(action);
        let params = this.setAndGetParams(hFilterJSON, displayOptions);
        this.getPipelineLockStatusByLoanType(params).subscribe(
            (data) => {
                this.getSummaryTablesData(data, cardData, property);
            },
            (err) => {
                cardData.simpleDataTableData.noDataType.action = NoDataActionType.error;
                this.getSummaryTablesData([], cardData, '');
                console.log(err);
            }
        );
    }

    getSummaryTablesData(data: IPipelineLoanStatus[], card: ICardData, fieldId: string) {
        let simpleDataTableData = card.simpleDataTableData;
        if (simpleDataTableData) {
            simpleDataTableData.hasDrilldown = false;
            simpleDataTableData.title = card.title;
            simpleDataTableData.isDataready = true;
            simpleDataTableData.hasData = data.length > 0;
            simpleDataTableData.noDataType.componentTitle = card.title;

            if (data && data.length > 0) {
                simpleDataTableData.queryParams['activeMilestones'] = 'All';
                simpleDataTableData.urlSegment = card.urlSegment;
                simpleDataTableData.hasAccess = this.checkAccess(
                    card.drilldownUrl + card.urlSegment
                );
                simpleDataTableData.baseUrl = card.drilldownUrl;
                simpleDataTableData.navFromUrl = card.navFromUrl;
                simpleDataTableData.showPercentages = card.showPercentages;

                simpleDataTableData.headerItems = [
                    {
                        fieldId: '',
                        value: '',
                        type: 'string',
                        style: {
                            'text-align': 'left',
                            width: '30%',
                            color: '#223540',
                        },
                    },
                    {
                        fieldId: '',
                        value: 'Units',
                        type: 'number',
                        style: {
                            'text-align': 'right',
                            width: '30%',
                            color: '#223540',
                            'padding-right': '30px',
                        },
                    },
                    {
                        fieldId: '',
                        value: 'Volume',
                        type: 'money',
                        style: {
                            'text-align': 'right',
                            width: '30%',
                            color: '#223540',
                        },
                    },
                ];
                simpleDataTableData.rowItems = [];

                let rowData = simpleDataTableData.rowItems;
                let uniqueNames = this._reusableFunctions.getUniqueItemList(data, fieldId);
                const totalUnits = data.reduce((a, { loanUnits }) => a + loanUnits, 0);
                const totalVolume = data.reduce((a, { loanVolume }) => a + loanVolume, 0);
                const percentData = this.getPercents(
                    data,
                    uniqueNames,
                    fieldId,
                    totalUnits,
                    totalVolume
                );

                uniqueNames.forEach((series, index) => {
                    let loanTypeData = data.filter((item) => item[fieldId] === series);
                    let sumVolume = this._reusableFunctions.calculateSumByProperty(
                        loanTypeData,
                        'loanVolume'
                    );
                    let sumUnits = this._reusableFunctions.calculateSumByProperty(
                        loanTypeData,
                        'loanUnits'
                    );

                    this.addDataTableRowData(
                        rowData,
                        series,
                        sumVolume,
                        sumUnits,
                        percentData,
                        index
                    );
                });

                simpleDataTableData.footerItems = [
                    {
                        fieldId: '',
                        value: 'Total Pipeline',
                        type: 'string',
                        style: {
                            'text-align': 'left',
                            width: '30%',
                            'border-top': '2px solid grey',
                            color: '#223540',
                            'font-weight': 'bold',
                        },
                    },
                    {
                        fieldId: '',
                        value: totalUnits ? totalUnits.toLocaleString() : '',
                        type: 'number',
                        style: {
                            'text-align': 'right',
                            width: '30%',
                            'border-top': '2px solid grey',
                            color: '#223540',
                            'font-weight': 'bold',
                            'padding-right': '30px',
                        },
                    },
                    {
                        fieldId: '',
                        value: totalVolume ? this._reusableFunctions.formatMoney(totalVolume) : '',
                        type: 'money',
                        style: {
                            'text-align': 'right',
                            width: '30%',
                            'border-top': '2px solid grey',
                            color: '#223540',
                            'font-weight': 'bold',
                        },
                    },
                ];
            }
        }
        card.simpleDataTableData = Object.assign({}, simpleDataTableData);
    }

    getPercents(
        data: IPipelineLoanStatus[],
        uniqueNames: string[],
        fieldId: string,
        totalUnits: number,
        totalVolume: number
    ) {
        const dataObj = {
            units: { numbers: [], percentages: [] },
            volume: { numbers: [], percentages: [] },
        };

        uniqueNames.forEach((series) => {
            let loanTypeData = data.filter((item) => item[fieldId] === series);

            dataObj.units.numbers.push(loanTypeData.reduce((a, { loanUnits }) => a + loanUnits, 0));
            dataObj.volume.numbers.push(
                loanTypeData.reduce((a, { loanVolume }) => a + loanVolume, 0)
            );
        });

        const rawUnitPercents = dataObj.units.numbers.map((number) => (number / totalUnits) * 100);
        const rawVolumePercents = dataObj.volume.numbers.map(
            (number) => (number / totalVolume) * 100
        );

        dataObj.units.percentages = this._reusableFunctions.getRefinedPercents(rawUnitPercents);
        dataObj.volume.percentages = this._reusableFunctions.getRefinedPercents(rawVolumePercents);

        return dataObj;
    }

    addDataTableRowData(
        rowData: Array<ITableDataItems[]>,
        seriesName: string,
        sumVolume: number,
        sumUnits: number,
        percentData,
        index: number
    ) {
        rowData.push([
            {
                fieldId: '',
                value: this._reusableFunctions.toCamelCase(seriesName),
                type: 'money',
                style: {
                    'text-align': 'left',
                    width: '30%',
                },
            },
            {
                fieldId: '',
                value: sumUnits ? sumUnits.toLocaleString() : '',
                type: 'money',
                style: {
                    'text-align': 'right',
                    width: '30%',
                    'padding-right': '30px',
                },
                percent: percentData.units.percentages[index],
            },
            {
                fieldId: '',
                value: sumVolume ? this._reusableFunctions.formatMoney(sumVolume) : '',
                type: 'money',
                style: {
                    'text-align': 'right',
                    width: '30%',
                },
                percent: percentData.volume.percentages[index],
            },
        ]);
    }
}

interface ISortingData {
    xVal: string;
    order: number;
}
